/**
 * Everything behind the report as downloadable rows. In the live report the server streams
 * these (a number on screen and the file always come from the same query); here they are
 * generated from the sample rows so the download behaviour can be judged now.
 */
import { downloadCSV, type CsvColumn } from '@/utils/csv';
import { DISPOSITION_TREE, alloc, buildCatalog, hash, rng } from './mockData';
import { BUCKETS } from './theme';
import { totals } from './stats';
import type { BucketKey, CallRow, CatalogCategory, Filters, WorkRow } from './types';

export type Rec = Record<string, string | number>;
export interface Dataset { name: string; rows: Rec[] }

const PRODUCTS = ['Sugar Balance Capsules', 'Liver Detox Drops', 'Cycle Care Capsules', 'Vigor Gold Capsules', 'Shilajit Resin', 'Daily Multi', 'Combo Pack'];
const when = (r: () => number, days = 30) => {
  const d = new Date(); d.setDate(d.getDate() - Math.floor(r() * days)); d.setHours(8 + Math.floor(r() * 11), Math.floor(r() * 60), 0, 0);
  return d.toISOString().slice(0, 16).replace('T', ' ');
};
const phone = (r: () => number) => `9${Math.floor(r() * 9) + 1}${Math.floor(10000000 + r() * 89999999)}`.slice(0, 10);

// ---- Customer timeline ------------------------------------------------------------------
// Every disposition update is kept, not only the current one. A customer updated five times
// has five complete L1 / L2 / L3 sets. In the wide files they are numbered oldest first, and
// the newest is repeated in the latest_* columns so nobody has to count across.

interface Update { ms: number; at: string; by: string; source: string; l1: string; l2: string; l3: string; remark: string }

const REMARKS = ['', '', 'Asked to call after 6 pm', 'Wants to compare price', 'Will pay on delivery', 'Number busy', 'Spoke to family member'];
const MAX_UPDATES = 8;

/** The disposition history of one work item or event, oldest first. Empty when never worked. */
function updatesFor(seed: string, bucket: BucketKey, agent: string, worked: boolean, startMs: number): Update[] {
  if (!worked) return [];
  const r = rng(hash(seed + 'tl'));
  const n = 1 + Math.floor(r() * r() * MAX_UPDATES * 1.3);       // most are updated once or twice, a few many times
  const tree = DISPOSITION_TREE[bucket];
  const out: Update[] = [];
  let t = startMs;
  for (let i = 0; i < Math.min(n, MAX_UPDATES); i++) {
    t += Math.round((0.2 + r() * 30) * 3600 * 1000);
    const leaf = tree[Math.floor(r() * tree.length)];
    const viaDialer = r() < 0.2;
    out.push({
      ms: t, at: new Date(t).toISOString().slice(0, 16).replace('T', ' '), by: agent, source: viaDialer ? 'TATA_TELE' : 'MANUAL',
      l1: leaf[0], l2: leaf[1], l3: leaf[2], remark: REMARKS[Math.floor(r() * REMARKS.length)],
    });
  }
  return out;
}

const fmtMs = (ms: number) => new Date(ms).toISOString().slice(0, 16).replace('T', ' ');

interface AbcCustomer { id: string; phone: string; events: number; worked: number; times: number[]; carts: number[]; updates: Update[] }

/**
 * ABC is counted as customers and events. One customer, many carts. Rule B: one disposition
 * covers every cart that arrived before it; a cart that arrives afterwards stays unworked
 * until the next update. So a customer with 3 pending carts and one update has 3 worked events.
 */
function abcCustomers(row: WorkRow): AbcCustomer[] {
  const r = rng(hash(row.agent + 'abccust'));
  const n = row.workItems;
  const extra = alloc(row.received - n, Array.from({ length: n }, () => 0.5 + r() * 2));
  const events = extra.map(e => 1 + e);
  const workedAlloc = alloc(row.worked, events);
  const letter = row.agent.slice(-1);
  return events.map((k, i) => {
    const id = `ABC-${letter}${String(i + 1).padStart(3, '0')}`;
    const worked = Math.min(workedAlloc[i], k);
    let t = Date.now() - Math.floor(2 + r() * 25) * 86400000;
    const times: number[] = [];
    for (let j = 0; j < k; j++) { t += Math.round((6 + r() * 40) * 3600000); times.push(t); }
    const updates = updatesFor(id, 'abc', row.agent, worked > 0, times[Math.max(worked - 1, 0)]);
    const lastUpdate = updates.length ? updates[updates.length - 1].ms : 0;
    for (let j = worked; j < k; j++) if (times[j] <= lastUpdate) times[j] = lastUpdate + (j - worked + 1) * 5 * 3600000;
    return { id, phone: phone(r), events: k, worked, times, carts: times.map(() => Math.round((600 + r() * 2400) / 10) * 10), updates };
  });
}

/** Wide timeline columns. Every row gets the same columns so the file opens cleanly in Excel. */
function timelineColumns(updates: Update[], width: number): Rec {
  const last = updates[updates.length - 1];
  const out: Rec = {
    update_count: updates.length,
    latest_update_no: updates.length || '',
    latest_at: last?.at ?? '', latest_by: last?.by ?? '', latest_source: last?.source ?? '',
    latest_l1: last?.l1 ?? '', latest_l2: last?.l2 ?? '', latest_l3: last?.l3 ?? '',
  };
  for (let k = 1; k <= width; k++) {
    const u = updates[k - 1];
    out[`update_${k}_at`] = u?.at ?? ''; out[`update_${k}_by`] = u?.by ?? ''; out[`update_${k}_source`] = u?.source ?? '';
    out[`update_${k}_l1`] = u?.l1 ?? ''; out[`update_${k}_l2`] = u?.l2 ?? ''; out[`update_${k}_l3`] = u?.l3 ?? '';
    out[`update_${k}_remark`] = u?.remark ?? '';
  }
  return out;
}

/** One row per work item (ABC: one per customer). */
export function workItemsData(rows: WorkRow[]): Dataset {
  const staged: { rec: Rec; updates: Update[] }[] = [];
  for (const row of rows) {
    const r = rng(hash(row.agent + row.bucket + 'wi'));
    if (row.bucket === 'abc') {
      // ABC is shown as customers: one row per customer, with how many carts and how many were covered by an update.
      for (const c of abcCustomers(row)) {
        staged.push({
          rec: {
            work_item_id: c.id, work_type: 'ABC (customer)', agent: row.agent, customer_phone: c.phone, received_at: fmtMs(c.times[0]),
            worked: c.worked > 0 ? 'Yes' : 'No', status: c.updates.length ? 'Closed by agent' : 'Open',
            abc_events: c.events, abc_events_worked: c.worked,
          },
          updates: c.updates,
        });
      }
      continue;
    }
    const n = row.workItems;
    const worked = row.worked;   // ABC was handled above, so the bucket's unit is the work item here
    for (let i = 0; i < n; i++) {
      const isWorked = i < worked;
      const received = when(r);
      const id = `${row.bucket.toUpperCase()}-${48000 + staged.length}`;
      staged.push({
        rec: {
          work_item_id: id, work_type: BUCKETS[row.bucket].label, agent: row.agent,
          customer_phone: phone(r), received_at: received, worked: isWorked ? 'Yes' : 'No',
          status: i < row.closedByAgent ? 'Closed by agent' : i >= n - row.closedBySystem ? 'Closed by system' : 'Open',
          abc_events: '', abc_events_worked: '',
        },
        updates: updatesFor(id, row.bucket as BucketKey, row.agent, isWorked, new Date(received.replace(' ', 'T')).getTime()),
      });
    }
  }
  const width = Math.max(1, ...staged.map(x => x.updates.length));
  // Every row carries the same columns, so ABC rows (which have the two ABC columns) and the rest line up.
  return { name: 'work-items', rows: staged.map(x => ({ abc_events: '', abc_events_worked: '', ...x.rec, ...timelineColumns(x.updates, width) })) };
}

/**
 * One row per ABC cart event, tied to its customer. `covered_by_update_no` says which of the
 * customer's disposition updates settled the event (Rule B); blank means it arrived after the
 * last update and is still unworked.
 */
export function abcEventsData(rows: WorkRow[]): Dataset {
  const staged: { rec: Rec; updates: Update[] }[] = [];
  for (const row of rows.filter(x => x.bucket === 'abc')) {
    for (const c of abcCustomers(row)) {
      for (let j = 0; j < c.events; j++) {
        const covered = j < c.worked;
        staged.push({
          rec: {
            event_id: `EV-${90000 + staged.length}`, abc_customer_id: c.id, customer_phone: c.phone, agent: row.agent,
            event_at: fmtMs(c.times[j]), cart_value: c.carts[j], worked: covered ? 'Yes' : 'No',
            covered_by_update_no: covered ? c.updates.findIndex(u => u.ms >= c.times[j]) + 1 : '',
          },
          updates: c.updates,
        });
      }
    }
  }
  const width = Math.max(1, ...staged.map(x => x.updates.length));
  return { name: 'abc-events', rows: staged.map(x => ({ ...x.rec, ...timelineColumns(x.updates, width) })) };
}

/**
 * The full customer timeline, one row per entry, oldest first. Disposition updates, calls,
 * remarks and ownership moves sit together, with a sequence number and an is_latest flag per
 * customer, so a customer updated five times shows five complete L1 / L2 / L3 rows.
 */
export function timelineData(rows: WorkRow[]): Dataset {
  const out: Rec[] = [];
  const customers = new Map<string, Rec[]>();
  for (const row of rows) {
    const r = rng(hash(row.agent + row.bucket + 'tlx'));
    const n = Math.min(row.workItems, 40);                    // sample: first 40 per agent and bucket
    for (let i = 0; i < n; i++) {
      const cust = phone(r);
      const start = Date.now() - Math.floor(r() * 25) * 86400000;
      const entries: Rec[] = [];
      const ups = updatesFor(`${row.agent}${row.bucket}${i}`, row.bucket as BucketKey, row.agent, true, start);
      ups.forEach(u => entries.push({
        entry_at: u.at, entry_type: 'Disposition update', agent: u.by, work_type: BUCKETS[row.bucket].label, source: u.source,
        l1: u.l1, l2: u.l2, l3: u.l3, detail: u.remark,
      }));
      if (r() < 0.6) entries.push({ entry_at: when(r, 20), entry_type: 'Call', agent: row.agent, work_type: BUCKETS[row.bucket].label, source: 'TATA_TELE', l1: '', l2: '', l3: '', detail: `${Math.floor(20 + r() * 300)} sec` });
      if (r() < 0.4) entries.push({ entry_at: when(r, 20), entry_type: 'Remark', agent: row.agent, work_type: BUCKETS[row.bucket].label, source: 'MANUAL', l1: '', l2: '', l3: '', detail: REMARKS[2 + Math.floor(r() * 5)] });
      if (r() < 0.15) entries.push({ entry_at: when(r, 20), entry_type: 'Owner changed', agent: '', work_type: '', source: 'SYSTEM', l1: '', l2: '', l3: '', detail: `to ${row.agent}` });
      customers.set(cust + '-' + out.length + '-' + i, entries.sort((a, b) => String(a.entry_at).localeCompare(String(b.entry_at))));
    }
  }
  for (const [key, entries] of customers) {
    const cust = key.split('-')[0];
    entries.forEach((e, idx) => out.push({ customer_phone: cust, seq: idx + 1, of: entries.length, is_latest: idx === entries.length - 1 ? 'Yes' : 'No', ...e }));
  }
  return { name: 'customer-timeline', rows: out };
}

/** One row per dial: who called whom, when, how it ended and for how long. */
export function callsData(calls: CallRow[]): Dataset {
  const out: Rec[] = [];
  for (const c of calls) {
    const r = rng(hash(c.agent + c.bucket + 'calls'));
    const outcomes: string[] = [
      ...Array(c.connected).fill('Connected'), ...Array(c.noAnswer).fill('No answer'), ...Array(c.cancelledRinging).fill('Cancelled while ringing'),
      ...Array(c.droppedOther).fill('Dropped / network'), ...Array(c.hungUpEarly).fill('Hung up early'),
    ];
    for (let i = outcomes.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [outcomes[i], outcomes[j]] = [outcomes[j], outcomes[i]]; }
    const avg = c.connected ? c.talkSeconds / c.connected : 0;
    const customers = Array.from({ length: Math.max(c.customersCalled, 1) }, () => phone(r));
    outcomes.forEach((o, i) => out.push({
      call_id: `CL-${70000 + out.length}`, called_at: when(r), agent: c.agent, work_type: BUCKETS[c.bucket].label,
      customer_phone: customers[i % customers.length], attempt_no: Math.floor(i / customers.length) + 1, outcome: o,
      connected: o === 'Connected' ? 'Yes' : 'No', talk_seconds: o === 'Connected' ? Math.max(5, Math.round(avg * (0.3 + r() * 1.4))) : 0,
      ring_seconds: Math.round(6 + r() * 20),
    }));
  }
  return { name: 'calls', rows: out };
}

/** One row per order, with the credited agent, bucket and attribution it carries. */
export function ordersData(rows: WorkRow[]): Dataset {
  const out: Rec[] = [];
  for (const row of rows) {
    const r = rng(hash(row.agent + row.bucket + 'ord'));
    const avg = row.validOrders ? row.validRevenue / row.validOrders : 1200;
    for (let i = 0; i < row.orders; i++) {
      const cancelled = i >= row.validOrders;
      out.push({
        order: `#ZH${633000 + out.length}`, order_date: when(r), credited_agent: row.agent, bucket: BUCKETS[row.bucket].label,
        attribution: i < row.tagOrders ? 'Agent Tag' : 'Coupon', product: PRODUCTS[Math.floor(r() * PRODUCTS.length)],
        units: 1 + Math.floor(r() * 2), status: cancelled ? 'Cancelled' : 'Valid',
        order_value: Math.round((avg * (0.75 + r() * 0.5)) / 10) * 10,
      });
    }
  }
  return { name: 'orders', rows: out };
}

/** Category > Product > Agent, flattened to one row per agent so it opens cleanly in Excel. */
export function catalogData(catalog: CatalogCategory[]): Dataset {
  const out: Rec[] = [];
  for (const c of catalog) for (const p of c.products) for (const a of p.agents) {
    out.push({
      category: c.category, product: p.product, agent: a.agent, orders: a.orders, valid_orders: a.valid, cancelled_orders: a.cancelled,
      units: a.units, valid_revenue: a.revenue, cancelled_value: a.cancelledValue,
      aov: a.valid ? Math.round(a.revenue / a.valid) : 0, work_items_abc_reorder: a.workItems,
    });
  }
  return { name: 'category-product-agent', rows: out };
}

/** The table on the current tab, as shown. */
export function summaryData(tab: string, rows: WorkRow[], filters: Filters, calls: CallRow[] = []): Dataset {
  if (tab === 'calls') {
    return { name: 'calls-summary', rows: calls.map(c => ({
      agent: c.agent, bucket: BUCKETS[c.bucket].label, dials: c.dials, connected: c.connected,
      connect_pct: c.dials ? Math.round((1000 * c.connected) / c.dials) / 10 : 0, not_connected: c.dials - c.connected,
      no_answer: c.noAnswer, cancelled_while_ringing: c.cancelledRinging, dropped_or_network: c.droppedOther, hung_up_early: c.hungUpEarly,
      talk_hours: Math.round((c.talkSeconds / 3600) * 10) / 10, avg_talk_seconds: c.connected ? Math.round(c.talkSeconds / c.connected) : 0,
      short_calls: c.shortCalls, customers_called: c.customersCalled, customers_connected: c.customersConnected,
      customers_never_reached: c.customersCalled - c.customersConnected,
    })) };
  }
  const catalog = buildCatalog(rows, filters.category, filters.product);
  if (tab === 'category') return { ...catalogData(catalog), name: 'category-product-agent-summary' };
  const out: Rec[] = rows.map(r => ({
    agent: r.agent, bucket: BUCKETS[r.bucket].label, work_items: r.workItems,
    worked: r.worked, unworked: r.received - r.worked, unique_customers: r.uniqueCustomers, unique_orders: r.uniqueOrders ?? '',
    orders: r.orders, valid_orders: r.validOrders, cancelled_orders: r.cancelledOrders, units: r.units,
    valid_revenue: r.validRevenue, cancelled_value: r.cancelledValue,
  }));
  if (tab === 'overview' || tab === 'work' || tab === 'orders' || tab === 'agents') {
    const t = totals(rows);
    out.push({ agent: 'TOTAL', bucket: '', work_items: t.workItems, worked: t.worked,
      unworked: t.unworked, unique_customers: t.uniqueCustomers, unique_orders: t.uniqueOrders,
      orders: t.orders, valid_orders: t.validOrders, cancelled_orders: t.cancelledOrders, units: t.units,
      valid_revenue: t.validRevenue, cancelled_value: t.cancelledValue });
  }
  return { name: `${tab}-summary`, rows: out };
}

export function fileStamp(f: Filters): string {
  const parts = [f.from, f.to];
  if (f.agent !== 'all') parts.push(f.agent.replace(/\W+/g, '-'));
  if (f.buckets.length) parts.push(f.buckets.join('-'));
  return parts.join('_');
}

export function saveDataset(ds: Dataset, filters: Filters): void {
  if (ds.rows.length === 0) return;
  const cols: CsvColumn<Rec>[] = Object.keys(ds.rows[0]).map(k => ({ header: k, accessor: k }));
  downloadCSV(`performance-report_${ds.name}_${fileStamp(filters)}`, cols, ds.rows);
}

/** Everything the report is built from, in one go. */
export function allDatasets(rows: WorkRow[], filters: Filters, calls: CallRow[]): Dataset[] {
  return [
    summaryData('agents', rows, filters),
    summaryData('calls', rows, filters, calls),
    workItemsData(rows), abcEventsData(rows), timelineData(rows), callsData(calls), ordersData(rows),
    catalogData(buildCatalog(rows, 'all', 'all')),
  ];
}

